import { foldDiacritics } from "@/lib/tool-kit/ciphers/alphabet";

/**
 * Keyword columnar transposition, single or double.
 *
 * Write the message in rows under the key, one character per column, then read the
 * columns off top to bottom in the alphabetical order of their key letters. Two equal
 * key letters are taken left to right, so LETTER numbers its columns 3 1 5 6 2 4. A key
 * may also be digits (3142), which sort before letters.
 *
 * - **Padding.** With a filler, the last row is filled out so every column has the same
 *   length. Without one the last row is short, the first few columns are one letter
 *   longer than the rest, and decoding works out which from the length alone.
 * - **Double transposition** puts the result through a second grid, with its own key
 *   (or the same one again). The filler, if any, completes the first grid only; the
 *   second grid takes that text as it is. That keeps decoding exact: the second step is
 *   undone from the length alone, and what it returns fills the first grid exactly.
 *
 * Every character given is moved — the caller decides beforehand, with
 * `applyTextOptions`, whether spaces and punctuation are part of the message.
 * Characters are code points, so an accented letter or an emoji moves as one.
 */

export const MAX_KEY_LENGTH = 40;

const KEY_CHAR = /[A-Z0-9]/;

/** The usable characters of a key, in capitals: letters (accents folded) and digits. */
export function keyChars(key: string): string[] {
  return [...foldDiacritics(key).toUpperCase()].filter((char) =>
    KEY_CHAR.test(char)
  );
}

/**
 * Each column's place in the reading order, 0-based: ZEBRAS → [5, 2, 1, 3, 0, 4].
 * Ties go left to right.
 */
export function columnRanks(key: string): number[] {
  const chars = keyChars(key);
  // Compared by code point, so digits sort before letters in every locale.
  const order = chars
    .map((char, column) => ({ char, code: char.charCodeAt(0), column }))
    .sort((a, b) => a.code - b.code || a.column - b.column);
  const ranks: number[] = Array.from({ length: chars.length }, () => 0);
  for (const [rank, { column }] of order.entries()) {
    ranks[column] = rank;
  }
  return ranks;
}

/** Column indexes in the order they are read: the inverse of `columnRanks`. */
export function readingOrder(ranks: readonly number[]): number[] {
  const order: number[] = Array.from({ length: ranks.length }, () => 0);
  for (const [column, rank] of ranks.entries()) {
    order[rank] = column;
  }
  return order;
}

/** Characters, filled out with `filler` to a multiple of `width`. */
export function pad(
  chars: readonly string[],
  width: number,
  filler: string
): string[] {
  const short = (width - (chars.length % width)) % width;
  return [...chars, ...Array.from({ length: short }, () => filler)];
}

/** One transposition: write in rows of `ranks.length`, read columns by rank. */
export function transposeOnce(
  chars: readonly string[],
  ranks: readonly number[]
): string[] {
  const width = ranks.length;
  const result: string[] = [];
  for (const column of readingOrder(ranks)) {
    for (let index = column; index < chars.length; index += width) {
      result.push(chars[index]);
    }
  }
  return result;
}

/** How many characters each column holds when `length` characters fill `width` columns. */
export function columnLengths(length: number, width: number): number[] {
  const fullRows = Math.floor(length / width);
  const longColumns = length % width;
  return Array.from({ length: width }, (_, column) =>
    column < longColumns ? fullRows + 1 : fullRows
  );
}

/** Undo one transposition, irregular last row included. */
export function untransposeOnce(
  chars: readonly string[],
  ranks: readonly number[]
): string[] {
  const width = ranks.length;
  const lengths = columnLengths(chars.length, width);
  const columns: string[][] = Array.from({ length: width }, () => []);
  let start = 0;
  for (const column of readingOrder(ranks)) {
    columns[column] = chars.slice(start, start + lengths[column]);
    start += lengths[column];
  }
  const result: string[] = [];
  const rows = Math.ceil(chars.length / width);
  for (let row = 0; row < rows; row += 1) {
    for (const column of columns) {
      if (row < column.length) {
        result.push(column[row]);
      }
    }
  }
  return result;
}

export interface ColumnarOptions {
  key: string;
  /** The second key for double transposition; null for a single transposition. */
  secondKey: string | null;
  /** The filler letter, or "" to leave the last row short. */
  filler: string;
}

/** The grid one transposition was worked in, for display. */
export interface TranspositionGrid {
  key: string[];
  ranks: number[];
  /** Row-major; `null` is an empty cell at the end of a short last row. */
  rows: (string | null)[][];
  /** How many cells at the end of the grid are filler. */
  fillers: number;
}

function gridOf(
  chars: readonly string[],
  key: string,
  fillers: number
): TranspositionGrid {
  const ranks = columnRanks(key);
  const width = ranks.length;
  const rows: (string | null)[][] = [];
  for (let start = 0; start < chars.length; start += width) {
    const row: (string | null)[] = chars.slice(start, start + width);
    while (row.length < width) {
      row.push(null);
    }
    rows.push(row);
  }
  return { key: keyChars(key), ranks, rows, fillers };
}

export interface ColumnarResult {
  output: string;
  /** The grids in the order they were filled: first key, then second key. */
  grids: TranspositionGrid[];
}

/** Both keys usable? A key needs at least two characters to move anything. */
export function keyUsable(key: string): boolean {
  const length = keyChars(key).length;
  return length >= 2 && length <= MAX_KEY_LENGTH;
}

export function columnarEncode(
  text: string,
  options: ColumnarOptions
): ColumnarResult {
  const ranks = columnRanks(options.key);
  const plain = [...text];
  const filled =
    options.filler === "" ? plain : pad(plain, ranks.length, options.filler);
  const fillers = filled.length - plain.length;
  const first = transposeOnce(filled, ranks);
  const grids = [gridOf(filled, options.key, fillers)];
  if (options.secondKey === null) {
    return { output: first.join(""), grids };
  }
  const second = transposeOnce(first, columnRanks(options.secondKey));
  grids.push(gridOf(first, options.secondKey, 0));
  return { output: second.join(""), grids };
}

export function columnarDecode(
  text: string,
  options: ColumnarOptions
): ColumnarResult {
  const cipher = [...text];
  const grids: TranspositionGrid[] = [];
  let firstCipher = cipher;
  if (options.secondKey !== null) {
    firstCipher = untransposeOnce(cipher, columnRanks(options.secondKey));
    grids.push(gridOf(firstCipher, options.secondKey, 0));
  }
  const plain = untransposeOnce(firstCipher, columnRanks(options.key));
  grids.unshift(gridOf(plain, options.key, 0));
  return { output: plain.join(""), grids };
}
